<template>
  <div class="st-surface">
    <StToolbar tone="canvas" class="st-surface__bar">
      <span class="st-surface__title">Armies</span>
    </StToolbar>

    <div class="st-surface__body">
      <!-- Rail 1: army picker -->
      <EditorSideList
        :items="ws.armies.value"
        :selected-index="armyIndex"
        add-label="Add army"
        empty-hint="No armies yet."
        width-key="studio:armyListWidth"
        @select="selectArmy"
        @add="openAdd('army')"
        @reorder="moveArmy"
      >
        <template #row="{ item }">{{ (item as AuthoredArmy).name || '(unnamed)' }}</template>
      </EditorSideList>

      <template v-if="army">
        <!-- Rail 2: segmented sections within the army -->
        <SegmentedSideList
          :sections="segSections"
          :army-label="army.name"
          :army-selected="node.kind === 'army'"
          :selected-key="selectedSectionKey"
          :selected-index="'index' in node ? node.index : -1"
          :selected-section-key="
            node.kind === 'enhancementCategory' ? ENH_PREFIX + node.category : null
          "
          add-section-label="Add enhancement category"
          :add-section-after-key="addCategoryAfterKey"
          width-key="studio:armySegWidth"
          @select-army="node = { kind: 'army' }"
          @select="onSelectSection"
          @select-section="onSelectCategory"
          @add="openAdd"
          @add-section="openAdd('__category__')"
          @reorder="onReorderSection"
        />

        <main class="st-surface__main">
          <!-- Army panel: header bar + Settings / Traits / Formations -->
          <div v-if="node.kind === 'army'" class="st-editor-card">
            <TitleBar
              :name="army.name"
              noun="Army"
              :entity-id="army.id"
              id-prefix="army"
              @rename="renameArmy"
              @duplicate="duplicateArmy"
              @delete="deleteArmy"
              @update:entity-id="updateArmy({ ...army, id: $event })"
            >
              <template #tabs>
                <StSegmentedControl v-model="armyTab" :options="armyTabs" size="sm" on-shell />
              </template>
            </TitleBar>
            <div class="st-editor-card__pane st-editor-card__pane--pad">
              <ArmySettingsForm
                v-if="armyTab === 'settings'"
                :model-value="army"
                @update:model-value="updateArmy"
              />
              <UpgradeGroupForm
                v-else-if="armyTab === 'traits'"
                hide-name
                rules-position="top"
                :model-value="traitsGroup"
                @update:model-value="updateTraits"
              />
              <ArmyPreview v-else :army="army" />
            </div>
          </div>

          <!-- Unit editor -->
          <div v-else-if="node.kind === 'unit' && army.units?.[node.index]" class="st-editor-card">
            <UnitEditor
              :model-value="army.units[node.index]"
              :army-keyword="army.keyword || army.name"
              :alliance="army.alliance"
              :enhancement-categories="enhancementCategories(army)"
              @update:model-value="updateUnit(node.index, $event)"
              @rename="renameUnit(node.index, $event)"
              @duplicate="duplicateUnit(node.index)"
              @delete="deleteUnit(node.index)"
            />
          </div>

          <!-- Path editor -->
          <div v-else-if="currentPath" class="st-editor-card">
            <PathEditor
              :model-value="currentPath"
              @update:model-value="updatePath"
              @rename="renamePath"
              @duplicate="duplicatePath"
              @delete="deletePath"
            />
          </div>

          <!-- Formation / lore group editor -->
          <div v-else-if="currentGroup" class="st-editor-card">
            <GroupEditor
              :model-value="currentGroup"
              :noun="groupNoun(node.kind)"
              :single-ability="node.kind === 'formation'"
              :hide-rules="node.kind === 'formation' || isLoreKind(node.kind)"
              :show-points="isLoreKind(node.kind)"
              :forced-ability-type="loreForcedType(node.kind)"
              :forced-keyword="loreForcedKeyword(node.kind)"
              @update:model-value="updateGroup"
              @rename="renameGroup"
              @duplicate="duplicateGroup"
              @delete="deleteGroup"
            />
          </div>

          <!-- Enhancement category editor (rename / delete the group) -->
          <div v-else-if="currentCategory" class="st-editor-card">
            <CategoryEditor
              :name="currentCategory"
              :keywords="currentCategoryRequirement.keywords"
              :non-keywords="currentCategoryRequirement.nonKeywords"
              @rename="renameCategory"
              @delete="deleteCategory"
              @update:requirement="updateCategoryRequirement"
            />
          </div>

          <!-- Enhancement table editor (category → table → upgrades) -->
          <div v-else-if="currentTable" class="st-editor-card">
            <EnhancementTableEditor
              :model-value="currentTable"
              @update:model-value="updateTable"
              @rename="renameTable"
              @duplicate="duplicateTable"
              @delete="deleteTable"
            />
          </div>
        </main>
      </template>

      <div v-else class="st-surface__placeholder">Select or add an army to begin.</div>
    </div>

    <NameModal
      :open="addTarget !== null"
      :title="addTitle"
      :placeholder="addPlaceholder"
      @confirm="handleAdd"
      @close="addTarget = null"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type {
  AuthoredArmy,
  IUnit,
  IUpgrade,
  IEnhancementGroup,
  IPath,
  EnhancementRequirement,
} from '@scrolldex/shared';
import {
  makeAuthoredUnit,
  makeUpgrade,
  makePath,
  makeEnhancementTable,
  enhancementRestrictionText,
  DEFAULT_ENHANCEMENT_CATEGORIES,
  AbilityType,
} from '@scrolldex/shared';
import { useWorkspace } from '../state/workspace.js';
import { StToolbar, StSegmentedControl, type SegmentOption } from '../components/ui/index.js';
import EditorSideList from '../components/EditorSideList.vue';
import SegmentedSideList, { type SegmentSection } from '../components/SegmentedSideList.vue';
import TitleBar from '../components/editor/TitleBar.vue';
import ArmySettingsForm from '../components/army/ArmySettingsForm.vue';
import UnitEditor from '../components/army/UnitEditor.vue';
import UpgradeGroupForm from '../components/army/UpgradeGroupForm.vue';
import GroupEditor from '../components/army/GroupEditor.vue';
import PathEditor from '../components/army/PathEditor.vue';
import EnhancementTableEditor from '../components/army/EnhancementTableEditor.vue';
import CategoryEditor from '../components/army/CategoryEditor.vue';
import ArmyPreview from '../components/army/ArmyPreview.vue';
import NameModal from '../components/NameModal.vue';
import { uniqueName } from '../utils/uniqueName.js';

// Plain IUpgrade-list group sections (formations + the three lores). Enhancements
// are NOT here — they're a deeper category → table → upgrade structure, keyed by
// `enh:<category>` in the side list and handled separately.
type LoreKey = 'spell' | 'prayer' | 'manifestation';
type GroupKey = LoreKey | 'formation';
type SectionKey = 'unit' | 'path' | GroupKey;
type Node =
  | { kind: 'army' }
  | { kind: 'unit'; index: number }
  | { kind: 'path'; index: number }
  | { kind: GroupKey; index: number }
  | { kind: 'enhancementCategory'; category: string }
  | { kind: 'enhancement'; category: string; index: number };

// Side-list key prefix marking an enhancement category section.
const ENH_PREFIX = 'enh:';

function isDefaultCategory(cat: string): boolean {
  return (DEFAULT_ENHANCEMENT_CATEGORIES as readonly string[]).includes(cat);
}

const ws = useWorkspace();
const armyIndex = ref(0);
const node = ref<Node>({ kind: 'army' });
const armyTab = ref<string | number>('settings');

const armyTabs: SegmentOption[] = [
  { value: 'settings', label: 'Settings', icon: 'gear' },
  { value: 'traits', label: 'Traits', icon: 'shield' },
  { value: 'preview', label: 'Preview', icon: 'eye' },
];

const army = computed<AuthoredArmy | null>(() => ws.armies.value[armyIndex.value] ?? null);

function updateArmy(next: AuthoredArmy) {
  ws.updateArmy(armyIndex.value, next);
}

// ─── Segmented sections (formations + enhancements + lores + units) ───────────
function loreList(a: AuthoredArmy, key: LoreKey): IUpgrade[] {
  return a.lores?.[key] ?? [];
}
function groupList(a: AuthoredArmy, key: GroupKey): IUpgrade[] {
  if (key === 'formation') return a.battleFormations ?? [];
  return loreList(a, key);
}
function withGroupList(a: AuthoredArmy, key: GroupKey, list: IUpgrade[]): AuthoredArmy {
  if (key === 'formation') return { ...a, battleFormations: list };
  return { ...a, lores: { ...(a.lores ?? {}), [key]: list } };
}
function pathList(a: AuthoredArmy): IPath[] {
  return a.paths ?? [];
}
function withPathList(a: AuthoredArmy, list: IPath[]): AuthoredArmy {
  return { ...a, paths: list };
}

// Enhancement categories: the two defaults always (Artefacts of Power, Heroic
// Traits), plus any extra categories present in the data (e.g. Monstrous Traits).
function enhancementCategories(a: AuthoredArmy): string[] {
  const out = [...DEFAULT_ENHANCEMENT_CATEGORIES] as string[];
  for (const c of Object.keys(a.enhancements ?? {})) if (!out.includes(c)) out.push(c);
  return out;
}
function enhancementTables(a: AuthoredArmy, cat: string): IEnhancementGroup[] {
  return a.enhancements?.[cat] ?? [];
}
function withEnhancementTables(
  a: AuthoredArmy,
  cat: string,
  tables: IEnhancementGroup[],
): AuthoredArmy {
  return { ...a, enhancements: { ...(a.enhancements ?? {}), [cat]: tables } };
}

const segSections = computed<SegmentSection[]>(() => {
  const a = army.value;
  if (!a) return [];
  // Order: formations, then each enhancement category (artefacts/heroic traits/
  // …), then the lores, with units last.
  const enh = enhancementCategories(a).map((cat) => ({
    key: ENH_PREFIX + cat,
    label: cat,
    items: enhancementTables(a, cat),
    // Custom categories are selectable (rename/delete); the two defaults are
    // permanent and act as plain headers.
    selectable: !isDefaultCategory(cat),
  }));
  return [
    { key: 'formation', label: 'Battle Formations', items: a.battleFormations ?? [] },
    { key: 'path', label: 'Paths', items: pathList(a), emptyHint: 'No paths yet.' },
    ...enh,
    { key: 'spell', label: 'Spell Lores', items: loreList(a, 'spell') },
    { key: 'prayer', label: 'Prayer Lores', items: loreList(a, 'prayer') },
    { key: 'manifestation', label: 'Manifestation Lores', items: loreList(a, 'manifestation') },
    { key: 'unit', label: 'Units', items: a.units ?? [], emptyHint: 'No units yet.' },
  ];
});

// The side-list section key that the currently-selected item belongs to (for
// highlighting the active row).
const selectedSectionKey = computed<string | null>(() => {
  const n = node.value;
  if (n.kind === 'enhancement') return ENH_PREFIX + n.category;
  if (n.kind === 'unit' || n.kind === 'path' || isGroupKind(n.kind)) return n.kind;
  return null;
});

// The "Add enhancement category" button renders after the last enhancement
// category section (i.e. above the lores), so it stays reachable no matter how
// many units sit at the bottom of the list.
const addCategoryAfterKey = computed(() => {
  const cats = army.value ? enhancementCategories(army.value) : [];
  return cats.length ? ENH_PREFIX + cats[cats.length - 1] : undefined;
});

function onSelectSection(key: string, index: number) {
  if (key.startsWith(ENH_PREFIX)) {
    node.value = { kind: 'enhancement', category: key.slice(ENH_PREFIX.length), index };
  } else {
    node.value = { kind: key as SectionKey, index } as Node;
  }
}
function onSelectCategory(key: string) {
  if (key.startsWith(ENH_PREFIX)) {
    node.value = { kind: 'enhancementCategory', category: key.slice(ENH_PREFIX.length) };
  }
}
function onReorderSection(key: string, from: number, to: number) {
  const a = army.value;
  if (!a) return;
  if (key.startsWith(ENH_PREFIX)) {
    const cat = key.slice(ENH_PREFIX.length);
    const tables = [...enhancementTables(a, cat)];
    const [m] = tables.splice(from, 1);
    tables.splice(to, 0, m);
    updateArmy(withEnhancementTables(a, cat, tables));
    if (node.value.kind === 'enhancement' && node.value.category === cat)
      node.value = { kind: 'enhancement', category: cat, index: to };
    return;
  }
  const k = key as SectionKey;
  if (k === 'unit') {
    const units = [...(a.units ?? [])];
    const [m] = units.splice(from, 1);
    units.splice(to, 0, m);
    updateArmy({ ...a, units });
    if (node.value.kind === 'unit') node.value = { kind: 'unit', index: to };
    return;
  }
  if (k === 'path') {
    const paths = [...pathList(a)];
    const [m] = paths.splice(from, 1);
    paths.splice(to, 0, m);
    updateArmy(withPathList(a, paths));
    if (node.value.kind === 'path') node.value = { kind: 'path', index: to };
    return;
  }
  const list = [...groupList(a, k)];
  const [m] = list.splice(from, 1);
  list.splice(to, 0, m);
  updateArmy(withGroupList(a, k, list));
  if (node.value.kind === k) node.value = { kind: k, index: to };
}

// ─── Army-level ops ───────────────────────────────────────────────────────────
function selectArmy(i: number) {
  armyIndex.value = i;
  node.value = { kind: 'army' };
  armyTab.value = 'settings';
}
function renameArmy(name: string) {
  if (army.value) updateArmy({ ...army.value, name });
}
function deleteArmy() {
  ws.deleteArmy(armyIndex.value);
  armyIndex.value = Math.max(0, Math.min(armyIndex.value, ws.armies.value.length - 1));
  node.value = { kind: 'army' };
}
function duplicateArmy() {
  const at = ws.duplicateArmy(armyIndex.value);
  if (at !== null) armyIndex.value = at;
  node.value = { kind: 'army' };
}
function moveArmy(from: number, to: number) {
  ws.moveArmy(from, to);
  if (armyIndex.value === from) armyIndex.value = to;
}

// ─── Traits (single fixed group, army-panel tab) ──────────────────────────────
// Battle traits are one fixed group ("Battle Traits: <army>"); formations are a
// named side-list collection (see groupList/segSections).
const blankTraits = makeUpgrade('Battle Traits');
const traitsGroup = computed<IUpgrade>(() => army.value?.battleTraits?.[0] ?? blankTraits);
function updateTraits(g: IUpgrade) {
  if (army.value) updateArmy({ ...army.value, battleTraits: [g] });
}

// ─── Units ────────────────────────────────────────────────────────────────────
function updateUnit(i: number, u: IUnit) {
  if (army.value)
    updateArmy({ ...army.value, units: (army.value.units ?? []).map((x, k) => (k === i ? u : x)) });
}
function renameUnit(i: number, name: string) {
  const u = army.value?.units?.[i];
  if (u) updateUnit(i, { ...u, name });
}
function deleteUnit(i: number) {
  if (!army.value) return;
  updateArmy({ ...army.value, units: (army.value.units ?? []).filter((_, k) => k !== i) });
  node.value = { kind: 'army' };
}
function duplicateUnit(i: number) {
  const u = army.value?.units?.[i];
  if (!u || !army.value) return;
  const copy = {
    ...JSON.parse(JSON.stringify(u)),
    name: uniqueName(
      u.name,
      (army.value.units ?? []).map((unit) => unit.name),
    ),
  } as IUnit;
  const units = [...(army.value.units ?? [])];
  units.splice(i + 1, 0, copy);
  updateArmy({ ...army.value, units });
  node.value = { kind: 'unit', index: i + 1 };
}

// ─── Paths ───────────────────────────────────────────────────────────────────
const currentPath = computed<IPath | null>(() => {
  const a = army.value;
  const n = node.value;
  if (!a || n.kind !== 'path') return null;
  return pathList(a)[n.index] ?? null;
});
function writePaths(mutate: (paths: IPath[]) => IPath[]) {
  const a = army.value;
  if (!a || node.value.kind !== 'path') return;
  updateArmy(withPathList(a, mutate(pathList(a))));
}
function updatePath(next: IPath) {
  const n = node.value;
  if (n.kind !== 'path') return;
  writePaths((list) => list.map((path, k) => (k === n.index ? next : path)));
}
function renamePath(name: string) {
  if (currentPath.value) updatePath({ ...currentPath.value, name });
}
function deletePath() {
  const n = node.value;
  if (n.kind !== 'path') return;
  writePaths((list) => list.filter((_, k) => k !== n.index));
  node.value = { kind: 'army' };
}
function duplicatePath() {
  const n = node.value;
  const a = army.value;
  if (n.kind !== 'path' || !currentPath.value || !a) return;
  const copy = {
    ...JSON.parse(JSON.stringify(currentPath.value)),
    name: uniqueName(
      currentPath.value.name,
      pathList(a).map((path) => path.name),
    ),
  } as IPath;
  writePaths((list) => {
    const next = [...list];
    next.splice(n.index + 1, 0, copy);
    return next;
  });
  node.value = { kind: 'path', index: n.index + 1 };
}

// ─── Formation / lore groups (plain IUpgrade lists) ───────────────────────────
function isGroupKind(k: Node['kind']): k is GroupKey {
  return k === 'spell' || k === 'prayer' || k === 'manifestation' || k === 'formation';
}
const currentGroup = computed<IUpgrade | null>(() => {
  const a = army.value;
  const n = node.value;
  if (!a || !isGroupKind(n.kind)) return null;
  return groupList(a, n.kind)[n.index] ?? null;
});
function groupNoun(kind: Node['kind']): string {
  if (kind === 'spell') return 'Spell Lore';
  if (kind === 'prayer') return 'Prayer Lore';
  if (kind === 'manifestation') return 'Manifestation Lore';
  if (kind === 'formation') return 'Battle Formation';
  return 'Entry';
}
// Lores can cost points, and their abilities are fixed by kind: spell and
// manifestation are always Spell-typed with the Spell keyword; prayers are
// Prayer-typed with the Prayer keyword.
function isLoreKind(kind: Node['kind']): boolean {
  return kind === 'spell' || kind === 'prayer' || kind === 'manifestation';
}
function loreForcedType(kind: Node['kind']): number | undefined {
  if (kind === 'spell' || kind === 'manifestation') return AbilityType.Spell;
  if (kind === 'prayer') return AbilityType.Prayer;
  return undefined;
}
function loreForcedKeyword(kind: Node['kind']): string | undefined {
  if (kind === 'spell' || kind === 'manifestation') return '**^^Spell^^**';
  if (kind === 'prayer') return '**^^Prayer^^**';
  return undefined;
}
function writeGroup(mutate: (list: IUpgrade[]) => IUpgrade[]) {
  const a = army.value;
  if (!a || !isGroupKind(node.value.kind)) return;
  const key = node.value.kind;
  updateArmy(withGroupList(a, key, mutate(groupList(a, key))));
}
function updateGroup(next: IUpgrade) {
  const n = node.value;
  if (!isGroupKind(n.kind)) return;
  const idx = n.index;
  writeGroup((list) => list.map((g, k) => (k === idx ? next : g)));
}
function renameGroup(name: string) {
  if (currentGroup.value) updateGroup({ ...currentGroup.value, name });
}
function deleteGroup() {
  const n = node.value;
  if (!isGroupKind(n.kind)) return;
  const idx = n.index;
  writeGroup((list) => list.filter((_, k) => k !== idx));
  node.value = { kind: 'army' };
}
function duplicateGroup() {
  const n = node.value;
  if (!isGroupKind(n.kind) || !currentGroup.value) return;
  const idx = n.index;
  const key = n.kind;
  const copy = {
    ...JSON.parse(JSON.stringify(currentGroup.value)),
    name: uniqueName(
      currentGroup.value.name,
      groupList(army.value, key).map((group) => group.name),
    ),
  } as IUpgrade;
  writeGroup((list) => {
    const next = [...list];
    next.splice(idx + 1, 0, copy);
    return next;
  });
  node.value = { kind: key, index: idx + 1 };
}

// ─── Enhancement tables (category → tables) ───────────────────────────────────
const currentTable = computed<IEnhancementGroup | null>(() => {
  const a = army.value;
  const n = node.value;
  if (!a || n.kind !== 'enhancement') return null;
  return enhancementTables(a, n.category)[n.index] ?? null;
});
function writeTables(mutate: (tables: IEnhancementGroup[]) => IEnhancementGroup[]) {
  const a = army.value;
  const n = node.value;
  if (!a || n.kind !== 'enhancement') return;
  updateArmy(withEnhancementTables(a, n.category, mutate(enhancementTables(a, n.category))));
}
function updateTable(next: IEnhancementGroup) {
  const n = node.value;
  if (n.kind !== 'enhancement') return;
  writeTables((list) => list.map((t, k) => (k === n.index ? next : t)));
}
function renameTable(name: string) {
  if (currentTable.value) updateTable({ ...currentTable.value, name });
}
function deleteTable() {
  const n = node.value;
  if (n.kind !== 'enhancement') return;
  writeTables((list) => list.filter((_, k) => k !== n.index));
  node.value = { kind: 'army' };
}
function duplicateTable() {
  const n = node.value;
  if (n.kind !== 'enhancement' || !currentTable.value) return;
  const copy = {
    ...JSON.parse(JSON.stringify(currentTable.value)),
    name: uniqueName(
      currentTable.value.name,
      enhancementTables(a, n.category).map((table) => table.name),
    ),
  } as IEnhancementGroup;
  writeTables((list) => {
    const next = [...list];
    next.splice(n.index + 1, 0, copy);
    return next;
  });
  node.value = { kind: 'enhancement', category: n.category, index: n.index + 1 };
}

// ─── Enhancement categories (the top group level) ─────────────────────────────
const currentCategory = computed<string | null>(() =>
  node.value.kind === 'enhancementCategory' ? node.value.category : null,
);
// The keyword filter for the open category (studio-only); empty when none set.
const currentCategoryRequirement = computed<EnhancementRequirement>(
  () =>
    army.value?.enhancementRequirements?.[currentCategory.value ?? ''] ?? {
      keywords: [],
      nonKeywords: [],
    },
);

// Persist the category's keyword filter and regenerate the (identical) restriction
// text on every table in the category so they stay in sync.
function updateCategoryRequirement(req: EnhancementRequirement) {
  const a = army.value;
  const cat = currentCategory.value;
  if (!a || !cat) return;
  const text = enhancementRestrictionText(req);
  const tables = (a.enhancements?.[cat] ?? []).map((t) => ({ ...t, restrictionText: text }));
  updateArmy({
    ...a,
    enhancements: { ...(a.enhancements ?? {}), [cat]: tables },
    enhancementRequirements: { ...(a.enhancementRequirements ?? {}), [cat]: req },
  });
}

function renameCategory(name: string) {
  const a = army.value;
  const n = node.value;
  const next = name.trim();
  if (!a || n.kind !== 'enhancementCategory' || !next || next === n.category) return;
  if (a.enhancements?.[next]) return; // category name already taken
  // Rebuild both records preserving key order, swapping the renamed key in place.
  const rebuilt: typeof a.enhancements = {};
  for (const [cat, tables] of Object.entries(a.enhancements ?? {})) {
    rebuilt[cat === n.category ? next : cat] = tables;
  }
  const rebuiltReq: NonNullable<AuthoredArmy['enhancementRequirements']> = {};
  for (const [cat, req] of Object.entries(a.enhancementRequirements ?? {})) {
    rebuiltReq[cat === n.category ? next : cat] = req;
  }
  updateArmy({ ...a, enhancements: rebuilt, enhancementRequirements: rebuiltReq });
  node.value = { kind: 'enhancementCategory', category: next };
}
function deleteCategory() {
  const a = army.value;
  const n = node.value;
  if (!a || n.kind !== 'enhancementCategory') return;
  const rebuilt: typeof a.enhancements = {};
  for (const [cat, tables] of Object.entries(a.enhancements ?? {})) {
    if (cat !== n.category) rebuilt[cat] = tables;
  }
  const rebuiltReq: NonNullable<AuthoredArmy['enhancementRequirements']> = {};
  for (const [cat, req] of Object.entries(a.enhancementRequirements ?? {})) {
    if (cat !== n.category) rebuiltReq[cat] = req;
  }
  updateArmy({ ...a, enhancements: rebuilt, enhancementRequirements: rebuiltReq });
  node.value = { kind: 'army' };
}

// ─── Add flow (modal) ─────────────────────────────────────────────────────────
const addTarget = ref<string | null>(null);
const addTitle = computed(() => {
  const t = addTarget.value;
  if (t === 'army') return 'New Army';
  if (t === 'unit') return 'New Unit';
  if (t === 'path') return 'New Path';
  if (t === 'formation') return 'New Battle Formation';
  if (t === '__category__') return 'New Enhancement Category';
  if (t?.startsWith(ENH_PREFIX)) return 'New Enhancement Table';
  return 'New Lore';
});
const addPlaceholder = computed(() => `${addTitle.value.replace('New ', '')} name…`);

function openAdd(target: string) {
  addTarget.value = target;
}
function handleAdd(name: string) {
  const target = addTarget.value;
  addTarget.value = null;
  if (!target) return;
  if (target === 'army') {
    ws.addArmy(name);
    armyIndex.value = ws.armies.value.length - 1;
    node.value = { kind: 'army' };
    armyTab.value = 'settings';
    return;
  }
  const a = army.value;
  if (!a) return;
  if (target === 'unit') {
    const units = [...(a.units ?? []), makeAuthoredUnit(name)];
    updateArmy({ ...a, units });
    node.value = { kind: 'unit', index: units.length - 1 };
    return;
  }
  if (target === 'path') {
    const paths = [...pathList(a), makePath(name)];
    updateArmy(withPathList(a, paths));
    node.value = { kind: 'path', index: paths.length - 1 };
    return;
  }
  if (target === '__category__') {
    const cat = name.trim();
    if (!cat || a.enhancements?.[cat]) return;
    updateArmy({ ...a, enhancements: { ...(a.enhancements ?? {}), [cat]: [] } });
    node.value = { kind: 'enhancementCategory', category: cat };
    return;
  }
  if (target.startsWith(ENH_PREFIX)) {
    const cat = target.slice(ENH_PREFIX.length);
    const tables = [...enhancementTables(a, cat), makeEnhancementTable(name)];
    updateArmy(withEnhancementTables(a, cat, tables));
    node.value = { kind: 'enhancement', category: cat, index: tables.length - 1 };
    return;
  }
  const key = target as GroupKey;
  const list = [...groupList(a, key), makeUpgrade(name)];
  updateArmy(withGroupList(a, key, list));
  node.value = { kind: key, index: list.length - 1 };
}
</script>
